import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const FloatingContainer = styled(Box)({
  position: 'fixed',
  bottom: '32px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: '20px',
  boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  minWidth: '600px',
});

const SelectionBadge = styled(Box)({
  backgroundColor: '#f0ebff',
  borderRadius: '33554400px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 16px',
});

const SelectionCount = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#5b2ff9',
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  lineHeight: '20px',
});

const ButtonsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const CancelButton = styled(Button)({
  border: '1px solid #e4e4e7',
  backgroundColor: 'transparent',
  color: '#262626',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  textTransform: 'none',
  padding: '16px 20px',
  borderRadius: '8px',
  height: '42px',
  width: '80px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#e4e4e7',
  },
});

const ConfirmButton = styled(Button)({
  backgroundColor: '#5b2ff9',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: '"Inter", "Noto Sans Arabic", sans-serif',
  textTransform: 'none',
  padding: '16px 20px',
  borderRadius: '10px',
  height: '42px',
  width: '120px',
  letterSpacing: '-0.1504px',
  '&:hover': {
    backgroundColor: '#4a25d4',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#999999',
  },
});

const WarningMessage = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#dc2626',
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  lineHeight: '16px',
  marginLeft: '12px',
});

interface MergeButtonProps {
  selectionCount: number;
  canMerge: boolean;
  onMerge: () => void;
  onClear: () => void;
  selectedChannelsCount: number;
  hasAirbnbSelected?: boolean;
  isLoading?: boolean;
}

const MergeButton: React.FC<MergeButtonProps> = memo(({
  selectionCount,
  canMerge,
  onMerge,
  onClear,
  hasAirbnbSelected = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  if (selectionCount === 0) {
    return null;
  }

  // Disable merge if no Airbnb unit is selected or loading
  const isMergeDisabled = !canMerge || !hasAirbnbSelected || isLoading;

  return (
    <FloatingContainer>
      <ButtonsContainer>
        <ConfirmButton
          onClick={onMerge}
          disabled={isMergeDisabled}
          >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: 'white', marginInlineEnd: '8px' }} />
            </>
          ) : (
            t('CHANNELS.UNITS_MAPPING.CONFIRM_MERGE', 'تأكيد الدمج')
          )}
        </ConfirmButton>
        <CancelButton onClick={onClear} disabled={isLoading}>
          {t('CHANNELS.UNITS_MAPPING.CANCEL', 'إلغاء')}
        </CancelButton>
        {!hasAirbnbSelected && selectionCount > 0 && !isLoading && (
          <WarningMessage>
            {t('CHANNELS.UNITS_MAPPING.AIRBNB_REQUIRED', 'يجب اختيار وحدة من Airbnb للدمج')}
          </WarningMessage>
        )}
      </ButtonsContainer>
      <SelectionBadge>
        <SelectionCount>
          {t('CHANNELS.UNITS_MAPPING.SELECTED_ITEMS', '{{count}} عناصر محددة', { count: selectionCount })}
        </SelectionCount>
      </SelectionBadge>
    </FloatingContainer>
  );
});

MergeButton.displayName = 'MergeButton';

export default MergeButton;
